import { type Event } from '@kalos/kalos-rpc';
import { type ReactNode, useMemo } from 'react';

import { JOB_STATUS_COLORS } from '../constants';
import { formatTimeFullUppercase } from '../tools/formatTimeUtils';
import { formatDate } from '../tools/helpers';
import { useEventBatchGetQuery, useEventQuery } from './react-query/useEventsQuery';
import { useTimesheetDepartmentListQuery } from './react-query/useTimesheetDepartmentQuery';

// BatchGet matches one value per field, so offering a second status (e.g. Archived) needs a
// second query merged client-side, or a backend change.
const CALLBACK_ORIGINAL_STATUS = 'Completed';

const hasScheduledTime = (t: string) => !!t && t !== '00:00:00' && t !== '00:00';

// event_rpc_view returns '0001-01-01 00:00:00' for a NULL date
const cleanDate = (s = '') => (s.trim().startsWith('000') ? '' : s.trim());

const jobNumber = (e: Event) => e.logJobNumber || String(e.id);

export interface CallbackJobOption {
  value: string;
  label: string;
  displayLabel: ReactNode;
  selectedLabel: string;
}

const toOption = (e: Event, departmentName?: string, status?: string): CallbackJobOption => {
  const showTimes = hasScheduledTime(e.timeStarted) && hasScheduledTime(e.timeEnded);
  const typeDetail = [e.jobSubtype, departmentName].filter(Boolean).join(' / ');
  const date = formatDate(cleanDate(e.dateStarted));

  return {
    value: String(e.id),
    label: [`#${jobNumber(e)}`, e.jobType, e.jobSubtype, e.description].join(' '),
    displayLabel: (
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-0.5 pr-2 sm:grid-cols-[8rem_11rem_7rem_minmax(0,1fr)] sm:items-center">
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{date || '—'}</p>
          {showTimes && (
            <p className="text-xs text-gray-500">
              {formatTimeFullUppercase(e.timeStarted)} – {formatTimeFullUppercase(e.timeEnded)}
            </p>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">{e.jobType || '—'}</p>
          {typeDetail && <p className="truncate text-xs text-gray-500">{typeDetail}</p>}
        </div>
        <div className="min-w-0">
          <p className="text-gray-900">#{jobNumber(e)}</p>
          {status && (
            <span className="flex items-center gap-1.5 text-xs text-gray-700">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: `#${JOB_STATUS_COLORS[status] || 'CCCCCC'}` }}
              />
              {status}
            </span>
          )}
        </div>
        {e.description && <p className="truncate text-gray-700">{e.description}</p>}
      </div>
    ),
    selectedLabel: [`#${jobNumber(e)}`, date, [e.jobType, e.jobSubtype].filter(Boolean).join(' / ')]
      .filter(Boolean)
      .join(' · '),
  };
};

export const useCallbackJobOptions = ({
  propertyId,
  excludeEventId = 0,
  selectedId = 0,
}: {
  propertyId: number;
  excludeEventId?: number;
  selectedId?: number;
}) => {
  const completedQuery = useEventBatchGetQuery({
    filters: {
      propertyId,
      isActive: 1,
      logJobStatus: CALLBACK_ORIGINAL_STATUS,
      withoutLimit: true,
      onlyEvents: true,
      orderBy: 'date_started',
      orderDir: 'DESC',
      ...(excludeEventId ? { id: excludeEventId, notEquals: ['Id'] } : {}),
    },
    enabled: !!propertyId,
  });
  const { data: departmentsData } = useTimesheetDepartmentListQuery({ filter: { isActive: 1 } });

  // An existing link can point at a job outside the list (an older callback linked to a Pending
  // or Canceled job); fetch it so the trigger keeps showing it.
  const completed = completedQuery.data?.results;
  const needsFallback = !!completed && !!selectedId && !completed.some((e) => e.id === selectedId);
  const fallbackQuery = useEventQuery({ filter: { id: selectedId }, enabled: needsFallback });
  // A disabled query still returns whatever another screen cached for this id
  const fallback = needsFallback ? fallbackQuery.data : undefined;
  const fallbackStatus = fallback?.logJobStatus.trim() ?? '';

  const options = useMemo(() => {
    const departmentNames = new Map(
      (departmentsData?.results ?? []).map((d) => [d.id, d.description]),
    );
    const rows = (completed ?? []).map((e) => toOption(e, departmentNames.get(e.departmentId)));
    return fallback
      ? [toOption(fallback, departmentNames.get(fallback.departmentId), fallbackStatus), ...rows]
      : rows;
  }, [completed, fallback, fallbackStatus, departmentsData]);

  // A Completed fallback (one at another property) is not a stale status.
  // Same rule as useRecentCompletedVisit: a completion date counts as completed, so the
  // recent-visit "Yes" pick (usually still in Admin Review) isn't flagged.
  const isStale =
    !!fallbackStatus &&
    fallbackStatus.toLowerCase() !== CALLBACK_ORIGINAL_STATUS.toLowerCase() &&
    !cleanDate(fallback?.logDateCompleted);
  // A job linked to itself is always wrong, whatever its status
  const isSelfLink = !!selectedId && selectedId === excludeEventId;

  return {
    options,
    isLoading: completedQuery.isLoading || fallbackQuery.isLoading,
    linkWarning: isSelfLink
      ? 'Linked job is this job.'
      : isStale
        ? `Linked job is ${fallbackStatus}. Callbacks should link a Completed job.`
        : undefined,
  };
};
